import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useEffect, useRef, useState } from 'react';
import { handleInitialPreferencesData } from '../../../controllers/cookie-controls/transformer';
import { getPreferences } from '../../../controllers/storage-preferences/get-preferences';
import { updatePreferences } from '../../../controllers/storage-preferences/update-preferences';
import { PrefetchedPreferenceState } from '../../../types';
/**
 * This hook is responsible for handling any initially provided preference data, and/or fetching missing preference
 * data from ConsentHub. It will then set the appropriate cookies and state for the user's preferences to be used
 * throughout the application.

 * @param (optional) initialPreferences - Any initially provided preferences from consuming applications (used for SSR)
 * @returns preferences - The current state of the user's preferences after fetching/transforming/setting cookies
 */
var usePreferences = function usePreferences(_ref) {
  var isInitialRender = _ref.isInitialRender,
    initialPreferences = _ref.initialPreferences;
  var prefetchedPreferencesData = useRef(initialPreferences);
  var _useState = useState(handleInitialPreferencesData(initialPreferences)),
    _useState2 = _slicedToArray(_useState, 2),
    preferences = _useState2[0],
    setPreferences = _useState2[1];
  useEffect(function () {
    // On initial render, this will either set the proper preferences in state + required cookies based on the
    // prefetched data, or fetch all of that data from ConsentHub and then set appropriate cookies + state.
    var loadStoredPrefs = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var prefs;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return getPreferences();
            case 2:
              prefs = _context.sent;
              // If something has gone wrong during this `get` process (invalid token, failed request, etc.)
              // these preferences will be undefined and we'll need to show the banner as we're in an unknown state
              setPreferences(prefs);

              // Set the initialRender flag to false so we can skip logic in banner rendering
              isInitialRender.current = false;
            case 5:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function loadStoredPrefs() {
        return _ref2.apply(this, arguments);
      };
    }();
    var unpackedPrefetchedPrefs = handleInitialPreferencesData(prefetchedPreferencesData.current);
    if (unpackedPrefetchedPrefs !== PrefetchedPreferenceState.INTERNAL) {
      // With a valid set of prefs, set them on the cookies, but don't save to ConsentHub
      updatePreferences(unpackedPrefetchedPrefs, undefined, true);
    }
    loadStoredPrefs();
  }, [isInitialRender]);
  return {
    preferences: preferences
  };
};
export default usePreferences;